import React from "react";
import { PaginationWrapper } from "./styles";

function buildPaginationInfo(totalItems, currentPage, perPage, rangeLength) {
  const totalPages = Math.ceil(totalItems / perPage);
  currentPage = parseInt(currentPage, 10) || 1;
  if (currentPage < 1) currentPage = 1;
  if (currentPage > totalPages) currentPage = totalPages;

  let firstPage = Math.max(1, currentPage - Math.floor(rangeLength / 2));
  let lastPage = Math.min(totalPages, currentPage + Math.floor(rangeLength / 2));

  if (lastPage - firstPage + 1 < rangeLength) {
    if (currentPage < totalPages / 2) {
      lastPage = Math.min(totalPages, lastPage + (rangeLength - (lastPage - firstPage)));
    } else {
      firstPage = Math.max(1, firstPage - (rangeLength - (lastPage - firstPage)));
    }
  }

  if (lastPage - firstPage + 1 > rangeLength) {
    if (currentPage > totalPages / 2) {
      firstPage++;
    } else {
      lastPage--;
    }
  }

  return {
    total_pages: totalPages,
    current_page: currentPage,
    first_page: firstPage,
    last_page: lastPage,
    previous_page: currentPage - 1,
    next_page: currentPage + 1,
    has_previous_page: currentPage > 1,
    has_next_page: currentPage < totalPages,
  };
}

function PageItem({ pageNumber, label, isActive, isDisabled, itemClass, linkClass, ariaLabel, onPageChange }) {
  const handleClick = (e) => {
    e.preventDefault();
    if (!isDisabled) onPageChange(pageNumber);
  };

  const liClass = [itemClass, isActive && "active", isDisabled && "disabled"]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={liClass}>
      <button
        className={linkClass}
        aria-label={ariaLabel}
        aria-current={isActive ? "page" : undefined}
        disabled={isDisabled}
        onClick={handleClick}
      >
        {label}
      </button>
    </li>
  );
}

/** Bootstrap-compatible pagination control. */
function Pagination({
  activePage = 1,
  itemsCountPerPage = 10,
  totalItemsCount,
  pageRangeDisplayed = 5,
  onChange,
  hideDisabled = false,
  itemClass,
  linkClass,
}) {
  const info = buildPaginationInfo(
    totalItemsCount,
    activePage,
    itemsCountPerPage,
    pageRangeDisplayed
  );

  const showPrevControls = !(hideDisabled && !info.has_previous_page);
  const showNextControls = !(hideDisabled && !info.has_next_page);

  const sharedProps = { itemClass, linkClass, onPageChange: onChange };

  return (
    <PaginationWrapper className="pagination">
      {showPrevControls && (
        <PageItem
          {...sharedProps}
          key="first"
          pageNumber={1}
          label="«"
          isActive={false}
          isDisabled={!info.has_previous_page}
          ariaLabel="Go to first page"
        />
      )}
      {showPrevControls && (
        <PageItem
          {...sharedProps}
          key="prev"
          pageNumber={info.previous_page}
          label="⟨"
          isActive={false}
          isDisabled={!info.has_previous_page}
          ariaLabel="Go to previous page"
        />
      )}
      {Array.from({ length: info.last_page - info.first_page + 1 }, (_, i) => {
        const page = info.first_page + i;
        return (
          <PageItem
            {...sharedProps}
            key={page}
            pageNumber={page}
            label={page}
            isActive={page === info.current_page}
            isDisabled={false}
            ariaLabel={`Go to page ${page}`}
          />
        );
      })}
      {showNextControls && (
        <PageItem
          {...sharedProps}
          key="next"
          pageNumber={info.next_page}
          label="⟩"
          isActive={false}
          isDisabled={!info.has_next_page}
          ariaLabel="Go to next page"
        />
      )}
      {showNextControls && (
        <PageItem
          {...sharedProps}
          key="last"
          pageNumber={info.total_pages}
          label="»"
          isActive={false}
          isDisabled={info.current_page === info.total_pages}
          ariaLabel="Go to last page"
        />
      )}
    </PaginationWrapper>
  );
}

export default Pagination;
